import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertDialog } from '@astryxdesign/core/AlertDialog';
import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { HStack } from '@astryxdesign/core/HStack';
import { MetadataList, MetadataListItem } from '@astryxdesign/core/MetadataList';
import { StatusDot } from '@astryxdesign/core/StatusDot';
import { Heading, Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/VStack';
import { useToast } from '@astryxdesign/core/Toast';
import { CalendarPlusIcon, CheckIcon, NavigationArrowIcon, PhoneIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { ErrorState, ListSkeleton } from '@/components/StateViews';
import { bookingQuery, cancelBooking } from '@/features/booking/api';
import { BookingStatusLine } from '@/features/booking/status';
import { NotificationOptIn } from '@/features/pwa/NotificationOptIn';
import { useTenant } from '@/features/tenant/context';
import { formatDateTime, formatMoney, formatTime } from '@/lib/format';
import { downloadIcs } from '@/lib/ics';
import { yandexRouteUrl } from '@/lib/maps';
import { forgetBooking, setBookingStatus } from '@/lib/storage';
import { errorMessage } from '@/lib/errors';

export function BookingDetailsPage() {
  const tenant = useTenant();
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const justCreated = params.get('created') === '1';
  const q = useQuery(bookingQuery(tenant.slug, token));
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const status = q.data?.status;
  useEffect(() => {
    if (status) setBookingStatus(tenant.slug, token, status);
  }, [status, tenant.slug, token]);
  const cancel = useMutation({
    mutationFn: () => cancelBooking(tenant.slug, token),
    onSuccess: (b) => {
      qc.setQueryData(bookingQuery(tenant.slug, token).queryKey, b);
      void qc.invalidateQueries({ queryKey: ['availability', tenant.slug] });
      setConfirmCancel(false);
      toast({ body: 'Запись отменена', type: 'info' });
    },
    onError: (e) => toast({ body: errorMessage(e), type: 'error' }),
  });

  return (
    <VStack as="main" gap={5} paddingInline={4} paddingBlockStart={4} className="app-page" maxWidth={720} style={{ marginInline: 'auto' }}>
      {q.isPending ? (
        <ListSkeleton rows={5} />
      ) : q.isError ? (
        <ErrorState title="Запись не открылась" error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          {justCreated && q.data.status === 'confirmed' ? (
            <VStack gap={3} align="center" paddingBlock={4}>
              <VStack className="app-success-badge" aria-hidden>
                <CheckIcon size={36} weight="bold" />
              </VStack>
              <VStack gap={1} align="center">
                <Heading level={2}>Вы записаны</Heading>
                <Text color="secondary" justify="center">Ждём вас в назначенное время. Запись сохранена на этом устройстве в разделе «Моя запись».</Text>
              </VStack>
            </VStack>
          ) : null}
          {q.data.is_demo ? <Banner status="warning" title="Демо-запись" description="Эта запись создана в демонстрационном режиме." /> : null}
          <VStack gap={1}>
            <HStack gap={2} align="center">
              <BookingStatusLine status={q.data.status} />
              <Text color="secondary">Код {q.data.public_code}</Text>
            </HStack>
            <Heading level={1}>{formatDateTime(q.data.starts_at, tenant.timezone, tenant.locale)}</Heading>
            <Text color="secondary">Готово примерно к {formatTime(q.data.ends_at, tenant.timezone, tenant.locale)}</Text>
          </VStack>
          <Card padding={4}>
            <MetadataList>
              <MetadataListItem label="Услуги">{q.data.items.map((i) => i.name).join(', ')}</MetadataListItem>
              <MetadataListItem label="Стоимость">{formatMoney(q.data.total_minor, q.data.currency, tenant.locale)}</MetadataListItem>
              <MetadataListItem label="Автомобиль">{`${q.data.vehicle.make_model}${q.data.vehicle.plate ? ` · ${q.data.vehicle.plate}` : ''} · ${q.data.vehicle_class}`}</MetadataListItem>
              <MetadataListItem label="Бокс">{q.data.resource_name}</MetadataListItem>
              <MetadataListItem label="Адрес">{q.data.tenant.address}</MetadataListItem>
            </MetadataList>
          </Card>

          {q.data.status === 'confirmed' ? (
            <VStack gap={3}>
              <HStack gap={2}>
              <Button
                label="В календарь"
                variant="secondary"
                width="100%"
                icon={<CalendarPlusIcon size={20} />}
                onClick={() =>
                  downloadIcs(
                    {
                      uid: `${q.data.id}@${tenant.slug}`,
                      start: q.data.starts_at,
                      end: q.data.ends_at,
                      title: `${tenant.name}: ${q.data.items.map((i) => i.name).join(', ')}`,
                      description: `Код записи ${q.data.public_code}. Телефон студии ${tenant.phone}`,
                      location: tenant.address,
                      url: window.location.href.split('?')[0],
                    },
                    `${tenant.slug}-${q.data.public_code}.ics`,
                  )
                }
              />
              {tenant.address ? <Button label="Маршрут" variant="secondary" width="100%" icon={<NavigationArrowIcon size={20} />} href={yandexRouteUrl(tenant.address)} target="_blank" /> : null}
              </HStack>
              <NotificationOptIn audience="customer" slug={tenant.slug} token={token} />
              {tenant.phone ? <Button label="Позвонить в студию" variant="ghost" width="100%" icon={<PhoneIcon size={20} />} href={`tel:${tenant.phone}`} /> : null}
              {q.data.can_cancel ? (
                <Button label="Отменить запись" variant="destructive" width="100%" onClick={() => setConfirmCancel(true)} />
              ) : (
                <Text color="secondary" size="sm">Отменить онлайн можно до {formatDateTime(q.data.cancel_until, tenant.timezone, tenant.locale)}. Позже — по телефону.</Text>
              )}
            </VStack>
          ) : (
            <HStack gap={2} align="center">
              <StatusDot variant="neutral" label="Запись неактивна" />
              <Button label="Убрать с устройства" variant="ghost" size="sm" onClick={() => { forgetBooking(tenant.slug, token); navigate(`/s/${tenant.slug}/my`, { replace: true }); }} />
            </HStack>
          )}
          <AlertDialog
            isOpen={confirmCancel}
            onOpenChange={setConfirmCancel}
            title="Отменить запись?"
            description="Время освободится для других клиентов."
            actionLabel="Отменить запись"
            cancelLabel="Не отменять"
            actionVariant="destructive"
            isActionLoading={cancel.isPending}
            onAction={() => cancel.mutate()}
          />
        </>
      )}
    </VStack>
  );
}
